import { useEffect } from 'react';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { IoMdClose } from 'react-icons/io';
import FormSchemaParser from 'components/Common/FormSchemaParser';
import Button from 'components/Common/Button';
import { useUpdateThirdPartyIntegration } from 'apis/agency/thirdPartyIntegrations/hook';
import { ThirdPartyIntegrationState } from 'apis/agency/thirdPartyIntegrations/types';
import { stripeThirdPartySchema } from 'utils/schema/validator/AgencyConfigureValidation';
import StripeThirdPartyForm from '../partials/StripeThirdPartyForm';

interface ThirdPartyIntegrationDrawerProps {
  open: boolean;
  onClose: () => void;
  state: ThirdPartyIntegrationState | null;
}

type ThirdPartyFormValues = { configuration: Record<string, unknown> };

interface DrawerFormProps {
  state: ThirdPartyIntegrationState;
  onClose: () => void;
}

function DrawerForm({ state, onClose }: DrawerFormProps) {
  const provider = state.provider;
  const isStripe = provider === 'stripe';
  const disableForm = isStripe && state.active;
  const { mutate: updateIntegration, isPending } = useUpdateThirdPartyIntegration(provider);

  const { control, handleSubmit, reset } = useForm<ThirdPartyFormValues>({
    resolver: isStripe ? zodResolver(stripeThirdPartySchema) : undefined,
    defaultValues: { configuration: state.configuration ?? {} }
  });

  useEffect(() => {
    reset({ configuration: state.configuration ?? {} });
  }, [state, reset]);

  const isConfigure = state.credentials_present;
  const title = `${isConfigure ? 'Configure' : 'Activate'} ${state.name}`;

  const onSubmit = (data: ThirdPartyFormValues) => {
    updateIntegration({ configuration: data.configuration ?? {} }, { onSuccess: () => onClose() });
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} display='flex' flexDirection='column' height='100%'>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography fontSize={18} fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <IoMdClose />
        </IconButton>
      </Stack>

      <Box p={3} flex={1} overflow='auto'>
        {isStripe ? (
          <StripeThirdPartyForm control={control} disabled={disableForm} />
        ) : (
          <FormSchemaParser
            fields={state.config_fields as any}
            control={control}
            namePrefix='configuration'
            gridSize={12}
          />
        )}
      </Box>

      <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
        <Button
          type='button'
          variant='outlined'
          color='inherit'
          onClick={onClose}
          sx={{
            borderRadius: '999px',
            px: 3,
            borderColor: '#ECECEC',
            bgcolor: '#fff',
            color: '#242424'
          }}
        >
          Cancel
        </Button>
        {!disableForm && (
          <Button type='submit' variant='contained' isPending={isPending} sx={{ borderRadius: '999px', px: 4 }}>
            Save
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export default function ThirdPartyIntegrationDrawer({ open, onClose, state }: ThirdPartyIntegrationDrawerProps) {
  if (!state) return null;
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 640 },
          borderRadius: 0
        }
      }}
    >
      <DrawerForm key={state.provider} state={state} onClose={onClose} />
    </Drawer>
  );
}
